import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCardIcon, BanknotesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface PayoutMethod {
  id: string;
  type: 'bank' | 'paypal';
  details: string;
  isDefault: boolean;
  verified: boolean;
}

const PayoutSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState<PayoutMethod[]>([]);
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState<'bank' | 'paypal'>('bank');
  const [details, setDetails] = useState('');

  useEffect(() => {
    fetchPayoutMethods();
  }, []);

  const fetchPayoutMethods = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/payout-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMethods(data.methods || []);
      }
    } catch (error) {
      console.error('Failed to load payout methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMethod = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/payout-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type, details }),
      });
      if (res.ok) {
        setDetails('');
        await fetchPayoutMethods();
      }
    } catch (error) {
      console.error('Failed to add payout method:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/payout-methods/${id}/set-default`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchPayoutMethods();
      }
    } catch (error) {
      console.error('Failed to set default payout method:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Payout Settings</h1>
      <p className="mb-6 text-sm text-gray-600">
        Manage how you receive payouts as a creator
      </p>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Payout Methods</h2>
        {methods.length === 0 ? (
          <div className="text-gray-500 text-sm">No payout methods added yet</div>
        ) : (
          <div className="space-y-4">
            {methods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                <div className="flex items-center space-x-3">
                  {method.type === 'bank' ? (
                    <BanknotesIcon className="w-6 h-6 text-green-600" />
                  ) : (
                    <CreditCardIcon className="w-6 h-6 text-indigo-600" />
                  )}
                  <span className="text-sm font-medium text-gray-900">{method.details}</span>
                  {method.verified && (
                    <CheckCircleIcon className="w-5 h-5 text-green-500 ml-2" title="Verified" />
                  )}
                  {method.isDefault && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800">Default</span>
                  )}
                </div>
                {!method.isDefault && (
                  <button
                    onClick={() => handleSetDefault(method.id)}
                    className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Set as Default
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Add Payout Method</h2>
        <form onSubmit={handleAddMethod} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'bank' | 'paypal')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="bank">Bank Account</option>
              <option value="paypal">PayPal</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Details</label>
            <input
              type="text"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={type === 'bank' ? 'Bank account number' : 'PayPal email address'}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={adding}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {adding ? 'Adding...' : 'Add Method'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PayoutSettingsPage;
